import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dashboard, PageLayout, useDashboardFeatureFlags } from '@components';
import { BackButton, Button, HeadContent, Icon, Skeleton, ToolbarButton, useMediaQueryContext, } from '@fourthwall/components';
import { DownloadIcon, FilterIcon } from '@fourthwall/icons';
import { isWebView } from '@fourthwall/utils';
import * as EmptyState from '@modules/Dashboard/components/EmptyState/EmptyState';
import { routing } from '@utils/routing';
import { AnalyticsCard } from '../../analyticsModule/components/AnalyticsCard/AnalyticsCard';
import { AnalyticsFiltersSelect } from '../../analyticsModule/components/AnalyticsFilterSelect/AnalyticsFiltersSelect';
import { AnalyticsFiltersButton } from '../../analyticsModule/components/AnalyticsFiltersButton/AnalyticsFiltersButton';
import { AnalyticsReportLayout } from '../../analyticsModule/components/AnalyticsReportLayout/AnalyticsReportLayout';
import { AnalyticsTable } from '../../analyticsModule/components/AnalyticsTable/AnalyticsTable';
import { AnalyticsToolbar } from '../../analyticsModule/components/AnalyticsToolbar/AnalyticsToolbar';
import { AnalyticsCompareDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsCompareDateRangeSelect/AnalyticsCompareDateRangeSelect';
import { AnalyticsDateRangeSelect } from '../../analyticsModule/components/tools/AnalyticsDateRangeSelect/AnalyticsDateRangeSelect';
import { AnalyticsGroupBySelect } from '../../analyticsModule/components/tools/AnalyticsGroupBySelect/AnalyticsGroupBySelect';
import { useAnalyticsParams } from '../../modules/AnalyticsParams/hooks/useAnalyticsParams';
import * as S from './AnalyticsReport.styled';
import { useAnalyticsReport } from './hooks/useAnalyticsReport';
export const AnalyticsReport = () => {
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const location = useLocation();
    const locationState = location.state;
    const media = useMediaQueryContext();
    const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
    const toggleFiltersPanel = () => setIsFiltersPanelOpen((state) => !state);
    const { enabled, isLoading, title, table, card, isExporting, onExport } = useAnalyticsReport();
    const params = useAnalyticsParams();
    const backButtonProps = (() => {
        if (locationState?.from) {
            return {
                label: locationState.from.label,
                to: {
                    pathname: locationState.from.location.pathname,
                    search: locationState.from.location.search,
                },
            };
        }
        return {
            label: 'Reports',
            to: routing.analyticsV2.reports.self,
        };
    })();
    if (!enabled) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HeadContent, { title: "Unknown report", titleTemplate: isWebView() ? title : undefined }),
            React.createElement(PageLayout, { size: "full" },
                React.createElement(Dashboard.Section, { topContent: React.createElement(BackButton, { ...backButtonProps }) },
                    React.createElement(EmptyState.Root, { title: "Report not found", subtitle: "We do not handle this report yet. Please try another one." })))));
    }
    if (isLoading) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HeadContent, { title: title, titleTemplate: isWebView() ? title : undefined }),
            React.createElement(PageLayout, { size: "full" },
                React.createElement(Dashboard.Section, { topContent: React.createElement(BackButton, { ...backButtonProps }) },
                    React.createElement(S.HeaderSkeleton, null,
                        React.createElement(Skeleton.Shape, { height: media.tablet ? 36 : 28, width: 250 }),
                        React.createElement(S.ToolbarContent, null, media.tablet ? (React.createElement(React.Fragment, null,
                            React.createElement(Skeleton.Shape, { height: 40, width: 100 }),
                            React.createElement(Skeleton.Shape, { height: 40, width: 100 }))) : (React.createElement(React.Fragment, null,
                            React.createElement(Skeleton.Shape, { height: 40, width: 40 }),
                            React.createElement(Skeleton.Shape, { height: 40, width: 40 }))))),
                    React.createElement(AnalyticsReportLayout, { toolbar: React.createElement(AnalyticsToolbar, { actions: React.createElement(S.Actions, null,
                                React.createElement(Skeleton.Shape, { height: 40, width: 100 }),
                                React.createElement(Skeleton.Shape, { height: 40, width: 100 })) },
                            React.createElement(Skeleton.Shape, { height: 38, width: 200 }),
                            React.createElement(Skeleton.Shape, { height: 38, width: 200 }),
                            React.createElement(Skeleton.Shape, { height: 38, width: 200 })) },
                        React.createElement(React.Fragment, null,
                            React.createElement(Skeleton.Shape, { height: 430 }),
                            React.createElement(Skeleton.Shape, { height: 300 })))))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HeadContent, { title: title, titleTemplate: isWebView() ? title : undefined }),
        React.createElement(PageLayout, { size: "full" },
            React.createElement(Dashboard.Section, { name: isWebView() ? undefined : title, topContent: React.createElement(BackButton, { ...backButtonProps }), toolbarContent: React.createElement(S.ToolbarContent, null, media.tablet ? (React.createElement(React.Fragment, null,
                    React.createElement(Button, { leftIcon: React.createElement(Icon, { component: DownloadIcon, height: 18 }), label: "Export", appearance: "secondary", size: "small", loading: isExporting, onClick: onExport }),
                    dashboardFeatureFlags.analyticsV2Filters.enabled && (React.createElement(AnalyticsFiltersButton, { onClick: toggleFiltersPanel })))) : (React.createElement(React.Fragment, null,
                    React.createElement(ToolbarButton, { icon: DownloadIcon, isLoading: isExporting, onClick: onExport }),
                    dashboardFeatureFlags.analyticsV2Filters.enabled && (React.createElement(ToolbarButton, { icon: FilterIcon, onClick: toggleFiltersPanel }))))) },
                React.createElement(AnalyticsReportLayout, { toolbar: React.createElement(AnalyticsToolbar, { actions: React.createElement(S.Actions, null,
                            React.createElement(Button, { leftIcon: React.createElement(Icon, { component: DownloadIcon, height: 18 }), label: "Export", appearance: "secondary", size: "small", loading: isExporting, onClick: onExport }),
                            dashboardFeatureFlags.analyticsV2Filters.enabled && (React.createElement(AnalyticsFiltersButton, { onClick: toggleFiltersPanel }))) },
                        React.createElement(AnalyticsDateRangeSelect, { ...params.dateRange }),
                        dashboardFeatureFlags.analyticsV2Compare.enabled && (React.createElement(AnalyticsCompareDateRangeSelect, { ...params.compareDateRange })),
                        React.createElement(AnalyticsGroupBySelect, { ...params.groupBy })), sidebar: React.createElement(AnalyticsFiltersSelect, { filters: [
                            {
                                title: 'Section 1',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                ],
                            },
                            {
                                title: 'Section 2',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                    { value: 'item4', label: 'Item 4', checked: false },
                                ],
                            },
                            {
                                title: 'Section 3',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                ],
                            },
                            {
                                title: 'Section 4',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                ],
                            },
                            {
                                title: 'Section 5',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                    { value: 'item4', label: 'Item 4', checked: false },
                                ],
                            },
                            {
                                title: 'Section 6',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                ],
                            },
                            {
                                title: 'Section 7',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                ],
                            },
                            {
                                title: 'Section 8',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                    { value: 'item4', label: 'Item 4', checked: false },
                                ],
                            },
                            {
                                title: 'Section 9',
                                items: [
                                    { value: 'item1', label: 'Item 1', checked: false },
                                    { value: 'item2', label: 'Item 2', checked: false },
                                    { value: 'item3', label: 'Item 3', checked: false },
                                ],
                            },
                        ], onChange: () => { } }), isSidebarOpen: isFiltersPanelOpen, hideSidebar: toggleFiltersPanel },
                    React.createElement(React.Fragment, null,
                        card && React.createElement(AnalyticsCard, { ...card }),
                        table && (React.createElement(S.AnalyticsTableWrapper, null,
                            React.createElement(AnalyticsTable, { ...table })))))))));
};
